// src/pages/Utilisateurs/PrepaPhysique/hooks/usePrepaPhysiqueController.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getAdherentsByUser } from "../../../../services/adherentServices";
import {
  getExercisesByAdherent,
  updateAdherentExerciseResult,
} from "../../../../services/prepaPhysiqueServices";

import {
  DEFAULT_PREPA_PHYSIQUE_STATE,
  PREPA_PHYSIQUE_STORAGE_KEY,
  buildPrepaDynamicOptions,
  computePrepaStats,
  filterPrepaAdherents,
  getExerciseDone,
  getExerciseRealisationDate,
  getExerciseRepetitions,
} from "../utils/prepaPhysiqueUtils";

function readStorageState() {
  try {
    const saved = localStorage.getItem(PREPA_PHYSIQUE_STORAGE_KEY);

    if (!saved) return DEFAULT_PREPA_PHYSIQUE_STATE;

    return {
      ...DEFAULT_PREPA_PHYSIQUE_STATE,
      ...JSON.parse(saved),
    };
  } catch {
    return DEFAULT_PREPA_PHYSIQUE_STATE;
  }
}

export default function usePrepaPhysiqueController() {
  const [loading, setLoading] = useState(true);
  const [adherents, setAdherents] = useState([]);
  const [exercisesByAdherent, setExercisesByAdherent] = useState({});
  const [savingKeys, setSavingKeys] = useState({});

  const [ui, setUi] = useState(readStorageState);
  const [searchInput, setSearchInput] = useState(ui.search || "");

  const persistUi = useCallback((nextState) => {
    setUi(nextState);
    localStorage.setItem(PREPA_PHYSIQUE_STORAGE_KEY, JSON.stringify(nextState));
  }, []);

  const updateUi = useCallback((patch) => {
    setUi((prev) => {
      const next = {
        ...prev,
        ...patch,
      };

      localStorage.setItem(PREPA_PHYSIQUE_STORAGE_KEY, JSON.stringify(next));

      return next;
    });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setUi((prev) => {
        if (prev.search === searchInput) return prev;

        const next = {
          ...prev,
          search: searchInput,
        };

        localStorage.setItem(PREPA_PHYSIQUE_STORAGE_KEY, JSON.stringify(next));

        return next;
      });
    }, 250);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const list = await getAdherentsByUser();
      const safeList = Array.isArray(list) ? list.filter(Boolean) : [];

      setAdherents(safeList);

      const entries = await Promise.all(
        safeList.map(async (adherent) => {
          try {
            const res = await getExercisesByAdherent(adherent.id);
            return [
              adherent.id,
              Array.isArray(res?.Exercices) ? res.Exercices.filter(Boolean) : [],
            ];
          } catch (error) {
            console.error(
              "Erreur chargement exercices adhérent :",
              adherent.id,
              error
            );

            return [adherent.id, []];
          }
        })
      );

      setExercisesByAdherent(Object.fromEntries(entries));
    } catch (error) {
      console.error("Erreur lors de la récupération des exercices :", error);
      toast.error("Erreur lors de la récupération des exercices.");
      setAdherents([]);
      setExercisesByAdherent({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const dynamicOptions = useMemo(
    () => buildPrepaDynamicOptions(exercisesByAdherent),
    [exercisesByAdherent]
  );

  const filteredAdherents = useMemo(
    () => filterPrepaAdherents(adherents, exercisesByAdherent, ui),
    [adherents, exercisesByAdherent, ui]
  );

  const stats = useMemo(
    () => computePrepaStats(exercisesByAdherent),
    [exercisesByAdherent]
  );

  const resetFilters = useCallback(() => {
    persistUi(DEFAULT_PREPA_PHYSIQUE_STATE);
    setSearchInput(DEFAULT_PREPA_PHYSIQUE_STATE.search);
  }, [persistUi]);

  const toggleExerciseDone = useCallback(
    async (adherentId, exercise) => {
      if (!adherentId || !exercise?.id) return;

      const key = `${adherentId}-${exercise.id}`;

      if (savingKeys[key]) return;

      const currentStatus = getExerciseDone(exercise);
      const newStatus = !currentStatus;

      setSavingKeys((prev) => ({
        ...prev,
        [key]: true,
      }));

      const currentDate = getExerciseRealisationDate(exercise);
      const dateRealisation =
        currentDate && currentDate !== "0000-00-00"
          ? currentDate
          : new Date().toISOString().slice(0, 10);

      const payload = {
        adherent_id: adherentId,
        prepa_physique_id: exercise.id,
        date_realisation: dateRealisation,
        repetitions: getExerciseRepetitions(exercise),
        is_done: newStatus,
      };

      try {
        const res = await updateAdherentExerciseResult(payload);

        if (res?.success === false) {
          toast.error("Erreur lors de la mise à jour.");
          return;
        }

        setExercisesByAdherent((prev) => {
          const currentList = Array.isArray(prev[adherentId])
            ? prev[adherentId]
            : [];

          const nextList = currentList.map((item) => {
            if (item.id !== exercise.id) return item;

            return {
              ...item,
              AdherentPrepaPhysique: {
                ...(item.AdherentPrepaPhysique || {}),
                is_done: newStatus,
                date_realisation: dateRealisation,
              },
            };
          });

          return {
            ...prev,
            [adherentId]: nextList,
          };
        });

        toast.success(
          newStatus
            ? "Exercice marqué comme réalisé."
            : "Exercice remis en non réalisé."
        );
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors de la mise à jour.");
      } finally {
        setSavingKeys((prev) => ({
          ...prev,
          [key]: false,
        }));
      }
    },
    [savingKeys]
  );

  return {
    loading,
    adherents,
    exercisesByAdherent,
    filteredAdherents,
    stats,
    ui,
    searchInput,
    setSearchInput,
    dynamicOptions,
    savingKeys,
    updateUi,
    resetFilters,
    toggleExerciseDone,
    refresh: loadData,
  };
}